import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { AnswerChoice, Fix, Motif, Question, Stage, TribePrivate } from "@hoa/protocol";
import { distanceM } from "@hoa/geo";
import { Balise, baliseActive } from "./balise.js";
import { Carte, CartePleine, type Itineraire } from "./Carte.js";
import { Plan } from "../plan/dessin.js";
import type { PlanPublic } from "./evenement.js";
import { Moai } from "./Moai.js";
import {
  Avatar,
  Barre,
  BoutonMoi,
  Bulle,
  Ecran,
  Envoyer,
  Etape,
  Feuille,
  Gain,
  Libelle,
  PastillePoints,
  Parole,
  Retour,
  dans,
  etToi,
  aHeure,
  heure,
  jour,
  minutesAPied,
  pluriel,
} from "./ui.js";
import type { SkinId } from "./skins.js";

export interface LigneChat {
  id: string;
  pid: string;
  pseudo: string;
  text: string;
  at: number;
  /** Une ligne de Hoa dans le fil (« Ana est sur place »), pas un message. */
  hoa?: boolean;
}

export interface PointsCommuns {
  questionText: string | null;
  answerText: string | null;
  avec: string[];
  autres: Array<{ questionText: string; answerText: string; avec: string[] }>;
}

/** L'étape que l'on regarde à la place de l'étape en cours (un appui sur son point). */
type Rouvert = null | "questions" | "chat" | "rdv" | "tribus";

/**
 * Changer de tribu sans quitter l'écran : la première étape de la frise
 * (« Tribu rejointe ») se rouvre sur la liste des tribus du quartier, ou des
 * tables de l'événement, comme avant d'en choisir une. Le bouton retour y
 * mène aussi ; il n'y a plus d'écran « autres tribus » à part, dont les
 * étapes n'étaient que des images.
 */
export interface ChoixTribu {
  /** « Tribus du 9e », ou le nom de l'événement. */
  titre: string;
  lignes: Array<{ id: string; titre: string; sous: string; couleur: string | null; compte: string; pleine: boolean }>;
  ouverte: boolean;
  onOuvrir: () => void;
  onFermer: () => void;
  onChoisir: (id: string) => void;
  onCreer?: () => void;
  /** Une table d'événement : on dit « table » plutôt que « tribu ». */
  table?: boolean;
}

const NOMBRES = ["personne", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit"];

function metres(d: number): string {
  return d < 1000 ? `${Math.max(10, Math.round(d / 10) * 10)} m` : `${(d / 1000).toFixed(1).replace(".", ",")} km`;
}

/**
 * L'écran de la tribu : toute la soirée sur une frise (planches H-2-Chat,
 * H-3-RDV, H-Retardataire, H-Relire, H-4-Reunis).
 *
 *   Questions de Hoa → Chat (15 min avant) → Rendez-vous → Réunis
 *
 * Hoa parle depuis l'étape en cours. Une étape passée se rouvre sur place
 * (« Relire », « Finir les questions ») : l'étape en cours se replie alors en
 * une pastille « En cours : … » qui ramène à elle. Rien ne défile, sauf
 * l'historique du chat, dont la fenêtre grandit avec les messages.
 */
export function Tribu(props: {
  skin: SkinId;
  tribe: TribePrivate;
  stage: Stage;
  pid: string | null;
  rejointA: number | null;
  questions: Question[];
  repondues: string[];
  chat: LigneChat[];
  points: PointsCommuns | null;
  distance: number | null;
  fix: Fix | null;
  maintenant: number;
  erreur: string | null;
  initiale: string;
  /** Points de l'appareil, et ce que rapporterait cette rencontre. */
  nPoints: number;
  gain: number;
  /** Le plan du stand et la session de cette table : au rendez-vous, il remplace la carte. */
  plan?: { plan: PlanPublic; sessionId: string } | null;
  onPoints: () => void;
  menuOuvert: boolean;
  signalerOuvert: boolean;
  dessus?: ReactNode;
  onMoi: () => void;
  onRetour: () => void;
  onFermerSignaler: () => void;
  onRepondre: (questionId: string, choix: AnswerChoice) => void;
  onEnvoyer: (texte: string) => void;
  onJySuis: () => void;
  onTerminer: () => void;
  onSignaler: (s: { cible?: string; messageId?: string; motif: Motif; eviter: boolean }) => void;
  onQuitter: () => void;
  choix?: ChoixTribu;
}): JSX.Element {
  const t = props.tribe;
  const [rouvertEtat, setRouvertEtat] = useState<Rouvert>(null);
  const rouvert: Rouvert = props.choix?.ouverte ? "tribus" : rouvertEtat;
  /** Ouvrir une étape (ou revenir à l'étape en cours) referme la liste des tribus. */
  const setRouvert = (r: Rouvert): void => {
    if (props.choix?.ouverte && r !== "tribus") props.choix.onFermer();
    setRouvertEtat(r === "tribus" ? null : r);
  };
  /** La tribu vers laquelle on s'apprête à partir (confirmation). */
  const [versAutre, setVersAutre] = useState<{ id: string | null; titre: string } | null>(null);
  const mot = props.choix?.table ? "table" : "tribu";
  /** La carte du rendez-vous en grand, par-dessus l'écran. */
  const [carteOuverte, setCarteOuverte] = useState(false);
  const [itineraire, setItineraire] = useState<Itineraire | null>(null);
  /** La couleur de la tribu en plein écran (balise.tsx). */
  const [balise, setBalise] = useState(false);
  const surItineraire = useCallback((i: Itineraire | null) => setItineraire(i), []);

  const aRepondre = props.questions.filter((q) => !props.repondues.includes(q.id));
  const questionsFinies = aRepondre.length === 0;
  const chatOuvert = t.phase === "chat";
  const arrive = props.stage === "gathered";
  const chatPasse = t.phase === "rendezvous" || t.phase === "reunie" || props.stage === "rendezvous" || arrive;
  const enRetard = props.rejointA !== null && props.rejointA >= t.rdvAt;
  /* Dans un événement, le chat peut durer au-delà du rendez-vous — ou toute
   * la soirée (`chatFinA` à null). En ville, il se ferme au rendez-vous. */
  const finDuChat = t.chatFinA === undefined ? t.rdvAt : t.chatFinA;
  const chatProlonge = t.chatFinA !== undefined && (t.chatFinA === null || t.chatFinA > props.maintenant) && t.phase !== "inscriptions" && t.phase !== "annulee";

  useEffect(() => {
    if (rouvert === "questions" && questionsFinies) setRouvert(null);
  }, [rouvert, questionsFinies]);

  /** L'étape où se trouve Hoa. */
  const courante: "questions" | "attente" | "chat" | "rdv" | "reunis" = arrive
    ? "reunis"
    : chatPasse
      ? "rdv"
      : chatOuvert
        ? "chat"
        : questionsFinies
          ? "attente"
          : "questions";

  // L'étape regardée devient l'étape en cours : on la montre, simplement.
  useEffect(() => {
    if ((rouvert === "chat" && courante === "chat") || (rouvert === "rdv" && courante === "rdv")) setRouvert(null);
  }, [rouvert, courante]);

  // La balise s'ouvre seule à l'arrivée, une fois ; elle se referme quand
  // tout le monde est là.
  const tousLa = t.members.length > 0 && t.members.every((m) => m.arrive);
  const baliseMontree = useRef(false);
  const avecBalise = baliseActive(t);
  useEffect(() => {
    if (arrive && avecBalise && !tousLa && !baliseMontree.current) {
      baliseMontree.current = true;
      setBalise(true);
    }
  }, [arrive, avecBalise, tousLa]);
  useEffect(() => {
    if (balise && (tousLa || !avecBalise)) setBalise(false);
  }, [balise, tousLa, avecBalise]);
  const boutonBalise =
    avecBalise && !tousLa ? (
      <button type="button" className="hoa-lien hoa-montrer-couleur" onClick={() => setBalise(true)}>
        <i style={{ background: t.color }} aria-hidden="true" />
        Montrer ma couleur
      </button>
    ) : null;

  /* En ville, le chat reste ouvert pendant la marche, jusqu'à ce que tout le
   * monde soit arrivé ; dans un événement, jusqu'à sa fin (chatProlonge). */
  const chatEcrivable =
    t.phase === "chat" || chatProlonge || (t.chatFinA === undefined && chatPasse && t.phase !== "annulee" && !t.members.every((m) => m.arrive));

  const d = itineraire?.distanceM ?? props.distance ?? (props.fix ? distanceM(props.fix, t.rendezvous.coords) : null);
  const membres = t.members.length;

  /** L'étape en cours, repliée en pastille quand on relit une étape passée. */
  const pastille = (texte: string): JSX.Element => (
    <button type="button" className="hoa-encours" onClick={() => setRouvert(null)}>
      En cours : {texte}
    </button>
  );
  const texteEnCours =
    courante === "rdv"
      ? `rendez-vous${d !== null && !t.rendezvous.surPlace ? `, ${metres(d)}` : ""}`
      : courante === "reunis"
        ? "réunis"
        : courante === "chat"
          ? "le chat"
          : courante === "questions"
            ? "les questions"
            : "l'attente du chat";

  /* ---------------- 0. Les tribus du quartier (changer de tribu) ---------------- */
  const etapeTribus =
    rouvert === "tribus" && props.choix ? (
      <Etape etat="faite" label={`Changer de ${mot}`}>
        <Libelle ouvert onClick={props.choix.onFermer}>
          {props.choix.titre}
        </Libelle>
        <div className="hoa-liste hoa-choix-tribu" style={{ marginTop: 8 }}>
          <button type="button" className="hoa-ligne active" onClick={props.choix.onFermer}>
            <i className="hoa-pastille" style={{ background: t.color }} aria-hidden="true" />
            <div>
              <b>{t.title}</b>
              <span>Ta {mot} · y revenir</span>
            </div>
          </button>
          {props.choix.lignes.map((l) => (
            <button key={l.id} type="button" className="hoa-ligne" disabled={l.pleine} onClick={() => setVersAutre(l)}>
              {l.couleur ? <i className="hoa-pastille" style={{ background: l.couleur }} aria-hidden="true" /> : null}
              <div>
                <b>{l.titre}</b>
                <span>{l.sous}</span>
              </div>
              <em>{l.compte}</em>
            </button>
          ))}
          {props.choix.onCreer ? (
            // Créer la sienne, c'est aussi quitter celle-ci : la même confirmation.
            <button type="button" className="hoa-ligne nouvelle" onClick={() => setVersAutre({ id: null, titre: "" })}>
              Créer la mienne
            </button>
          ) : null}
        </div>
        {props.choix.lignes.length === 0 ? (
          <p className="hoa-note" style={{ marginTop: 8 }}>
            {props.choix.table ? "Aucune autre table pour l'instant." : "Aucune autre tribu pour l'instant."}
          </p>
        ) : null}
      </Etape>
    ) : null;

  /* ---------------- 1. Les questions de Hoa ---------------- */
  const etapeQuestions = (() => {
    const ouverte = rouvert === "questions" || (rouvert === null && courante === "questions");
    if (ouverte) {
      const q = aRepondre[0];
      const index = props.questions.length - aRepondre.length;
      return (
        <Etape etat="courante" expression="question" label="Les questions de Hoa">
          {q ? (
            <>
              <Bulle>
                <small className="hoa-surtitre">
                  Question {index + 1} sur {props.questions.length}
                </small>
                {q.text}
              </Bulle>
              <div className="hoa-reponses" role="group" aria-label={q.text}>
                {(["a", "b", "c"] as const).map((c) => (
                  <button key={`${q.id}-${c}`} type="button" className="hoa-reponse" onClick={() => props.onRepondre(q.id, c)}>
                    {q[c]}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </Etape>
      );
    }
    if (courante === "questions") {
      return (
        <Etape etat="courante" expression="question" label="Les questions de Hoa">
          {pastille(texteEnCours)}
        </Etape>
      );
    }
    if (rouvert === "tribus") return null;
    return (
      <Etape etat="faite">
        <Libelle onClick={props.choix?.onOuvrir}>
          {mot === "table" ? "Table rejointe" : "Tribu rejointe"}
          {enRetard && props.rejointA ? ` à ${heure(props.rejointA)}` : ""}
          {!questionsFinies ? ` · ${props.questions.length - aRepondre.length}/${props.questions.length} réponses` : ""}
        </Libelle>
        {!questionsFinies && !arrive && rouvert === null ? (
          <button type="button" className="hoa-relire" onClick={() => setRouvert("questions")}>
            Finir les questions
          </button>
        ) : null}
      </Etape>
    );
  })();

  /* ---------------- 2. Le chat ---------------- */
  const etapeChat = (() => {
    if (rouvert === "chat") {
      return (
        <Etape etat="faite" label="Chat de la tribu">
          <Libelle ouvert onClick={() => setRouvert(null)}>
            {chatEcrivable ? `Chat de la ${mot}` : "Chat fermé"}
          </Libelle>
          <Historique lignes={props.chat} moi={props.pid} tribe={t} boite />
          {chatEcrivable ? <Saisie onEnvoyer={props.onEnvoyer} mot={mot} /> : null}
        </Etape>
      );
    }
    if (rouvert === null && courante === "attente") {
      const manque = Math.max(0, t.minimum - membres);
      return (
        <Etape etat="courante" expression="neutre" label="En attendant le chat">
          <Bulle>
            Merci. Le chat s'ouvre {aHeure(t.chatOpensAt, props.maintenant).startsWith("à ") ? "" : `${jour(t.chatOpensAt, props.maintenant)} `}à
            <big>{heure(t.chatOpensAt)}</big>
            {dans(t.chatOpensAt, props.maintenant)}.{" "}
            {manque > 0
              ? `Vous êtes ${membres}, il en faut ${t.minimum} pour partir : sinon j'annule et je te préviens.`
              : `Vous êtes ${membres}. La tribu part.`}
          </Bulle>
          <Membres tribe={t} />
        </Etape>
      );
    }
    if (rouvert === null && courante === "chat") {
      const minutes = finDuChat === null ? 0 : Math.max(1, Math.ceil((finDuChat - props.maintenant) / 60_000));
      return (
        <Etape etat="courante" expression="sourire" label="Chat de la tribu">
          <Bulle>
            {finDuChat === null
              ? mot === "table"
                ? "Le chat est ouvert."
                : "Le chat reste ouvert toute la soirée."
              : `Le chat reste ouvert ${minutes} ${pluriel(minutes, "minute", "minutes")}.`}{" "}
            {/* À une table, on se retrouve à la table : pas besoin de signe pour se reconnaître. */}
            {mot === "table" ? "Présentez-vous en quelques mots." : "Dites-vous comment vous reconnaître."}
          </Bulle>
          <Historique lignes={props.chat} moi={props.pid} tribe={t} />
          <Saisie onEnvoyer={props.onEnvoyer} mot={mot} />
        </Etape>
      );
    }
    if (courante === "chat") {
      // On relit les questions pendant le chat : le chat attend, replié.
      return (
        <Etape etat="courante" expression="sourire" label="Chat de la tribu">
          {pastille("le chat")}
        </Etape>
      );
    }
    if (chatPasse && chatProlonge && rouvert === null) {
      // Le rendez-vous est passé, mais le chat de l'événement continue.
      return (
        <Etape etat="faite" label="Chat de la tribu">
          <span className="hoa-ligne-etape">
            <Libelle>{finDuChat === null ? (mot === "table" ? "Chat ouvert" : "Chat ouvert toute la soirée") : `Chat ouvert jusqu'à ${heure(finDuChat)}`}</Libelle>
          </span>
          <Historique lignes={props.chat} moi={props.pid} tribe={t} boite />
          <Saisie onEnvoyer={props.onEnvoyer} mot={mot} />
        </Etape>
      );
    }
    if (chatPasse) {
      return (
        <Etape etat="faite">
          <Libelle onClick={() => setRouvert("chat")}>{chatEcrivable ? `Chat de la ${mot}` : "Chat fermé"}</Libelle>
        </Etape>
      );
    }
    if (courante === "attente" && rouvert !== null) {
      return (
        <Etape etat="courante" expression="neutre" label="En attendant le chat">
          {pastille(texteEnCours)}
        </Etape>
      );
    }
    return (
      <Etape etat="avenir">
        <Libelle>Chat de la {mot}, dès {heure(t.chatOpensAt)}</Libelle>
      </Etape>
    );
  })();

  /* ---------------- 3. Le rendez-vous ---------------- */
  const planDuStand = props.plan ? (
    <div className="hoa-plan">
      <Plan plan={props.plan.plan} vive={(e) => (e as { sessionId?: string | null }).sessionId === props.plan?.sessionId} titre={`Plan du stand : ${t.tribeName}`} />
    </div>
  ) : null;

  const etapeRdv = (() => {
    const lieu = `${t.rendezvous.name}${t.rendezvous.landmark ? `, ${t.rendezvous.landmark}` : ""}`;
    if (courante === "rdv") {
      if (rouvert !== null) {
        return (
          <Etape etat="courante" expression="pouce" label="Rendez-vous">
            {pastille(texteEnCours)}
          </Etape>
        );
      }
      const deja = t.members.filter((m) => m.arrive && !m.moi).map((m) => m.pseudo);
      const surPlace = t.rendezvous.surPlace === true;
      const qui =
        deja.length === 0
          ? `Rendez-vous ${lieu}.`
          : enRetard
            ? `Tu arrives en cours de route. ${deja.length === 1 ? `${deja[0]} est` : `Ils sont ${NOMBRES[deja.length] ?? deja.length}`} ${deja.length === 1 ? "déjà " : ""}devant ${lieu}.`
            : `${deja.length === 1 ? `${deja[0]} est` : `${etToi(deja).replace(/ et toi$/, "")} sont`} déjà devant ${lieu}.`;
      return (
        <Etape etat="courante" expression="pouce" label="Rendez-vous">
          <Bulle>
            {qui}
            {surPlace || d === null ? (
              <> Rendez-vous {aHeure(t.rdvAt, props.maintenant)}.</>
            ) : (
              <>
                {" "}Il te reste
                <big className="xl">{metres(d)}</big>
                environ {minutesAPied(d)} {pluriel(minutesAPied(d), "minute", "minutes")} à pied.
              </>
            )}
          </Bulle>
          {!surPlace ? <Carte fix={props.fix} cible={t.rendezvous.coords} skin={props.skin} onItineraire={surItineraire} onAgrandir={() => setCarteOuverte(true)} /> : null}
          {surPlace && planDuStand}
          {props.erreur ? (
            <div className="hoa-erreur" role="alert" style={{ marginTop: 10 }}>
              {props.erreur}
            </div>
          ) : null}
          <button type="button" className="hoa-go" style={{ marginTop: 12 }} onClick={props.onJySuis}>
            J'y suis
          </button>
          {boutonBalise}
        </Etape>
      );
    }
    const titre = arrive ? `Rendez-vous, ${t.rendezvous.name}` : `Rendez-vous ${aHeure(t.rdvAt, props.maintenant)}`;
    // Sur place (événement), il n'y a pas de chemin à montrer : le plan du stand, s'il est dessiné.
    if (t.rendezvous.surPlace && planDuStand) {
      return (
        <Etape etat={arrive ? "faite" : "avenir"} label="Rendez-vous">
          <Libelle ouvert={rouvert === "rdv"} onClick={() => setRouvert(rouvert === "rdv" ? null : "rdv")}>
            {titre}
          </Libelle>
          {rouvert === "rdv" ? planDuStand : null}
        </Etape>
      );
    }
    if (t.rendezvous.surPlace) {
      return (
        <Etape etat={arrive ? "faite" : "avenir"}>
          <Libelle>{titre}</Libelle>
        </Etape>
      );
    }
    if (rouvert === "rdv") {
      return (
        <Etape etat={arrive ? "faite" : "avenir"} label="Rendez-vous">
          <Libelle ouvert onClick={() => setRouvert(null)}>
            {titre}
          </Libelle>
          <p className="hoa-rdv-detail">
            <b>{lieu}</b>
            {d !== null ? (
              <>
                {" · "}
                {metres(d)}, environ {minutesAPied(d)} {pluriel(minutesAPied(d), "minute", "minutes")} à pied
              </>
            ) : null}
          </p>
          <Carte fix={props.fix} cible={t.rendezvous.coords} skin={props.skin} onItineraire={surItineraire} onAgrandir={() => setCarteOuverte(true)} />
        </Etape>
      );
    }
    return (
      <Etape etat={arrive ? "faite" : "avenir"}>
        <Libelle onClick={() => setRouvert("rdv")}>{titre}</Libelle>
      </Etape>
    );
  })();

  /* ---------------- 4. Réunis ---------------- */
  const etapeReunis = (() => {
    if (!arrive) {
      return (
        <Etape etat="avenir" dernier>
          <Libelle>Réunis</Libelle>
        </Etape>
      );
    }
    if (rouvert !== null) {
      return (
        <Etape etat="courante" expression="neutre" dernier label="Réunis">
          {pastille(texteEnCours)}
        </Etape>
      );
    }
    if (t.phase !== "reunie" || !props.points) {
      return (
        <Etape etat="courante" expression="sourire" dernier label="Réunis">
          <Bulle>Tu es le premier sur place. Je te fais signe dès que quelqu'un arrive.</Bulle>
          <Membres tribe={t} arrives />
          {boutonBalise}
        </Etape>
      );
    }
    const p = props.points;
    const tous = t.members.every((m) => m.arrive);
    return (
      <Etape etat="courante" expression="joie" dernier libre anime label="Réunis">
        <Bulle>
          {/* À une table, c'est la marque qui parle : Hoa n'a pas à se dire « libre ». */}
          {tous ? "Vous êtes tous là" : "Vous êtes là"}
          {mot === "table" ? " !" : ", je suis libre !"}{" "}
          {p.answerText ? (
            <>
              Pour commencer, parlez de
              {p.questionText ? <small className="hoa-rappel">{p.questionText}</small> : null}
              <big>{p.answerText}</big>
              {etToi(p.avec)} l'avez choisi.
            </>
          ) : (
            <>Aucune réponse en commun : c'est un bon début. {t.mission}</>
          )}
        </Bulle>
        {props.gain > 0 ? <Gain n={props.gain} /> : null}
        {boutonBalise}
        {p.autres.length > 0 ? (
          <div className="hoa-liste">
            {p.autres.map((x) => (
              <div key={x.questionText} className="hoa-ligne">
                <div>
                  <small className="hoa-rappel">{x.questionText}</small>
                  <b>{x.answerText}</b>
                  <span>{etToi(x.avec)}</span>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </Etape>
    );
  })();

  return (
    <Ecran
      skin={props.skin}
      label={`Tribu ${t.title}`}
      dessus={
        <>
          {props.signalerOuvert ? (
            <Signaler
              tribe={t}
              chat={props.chat}
              onFermer={props.onFermerSignaler}
              onEnvoyer={(s) => {
                props.onSignaler(s);
                props.onFermerSignaler();
              }}
              onQuitter={() => {
                props.onFermerSignaler();
                props.onQuitter();
              }}
            />
          ) : null}
          {versAutre && props.choix ? (
            <Feuille label={`Changer de ${mot}`} onFermer={() => setVersAutre(null)}>
              <Parole expression="question">
                {versAutre.id === null
                  ? `Créer ta tribu te fera quitter « ${t.title} ». On continue ?`
                  : `Tu quittes « ${t.title} » pour « ${versAutre.titre} » ?`}
              </Parole>
              <div className="hoa-pile">
                <button
                  type="button"
                  className="hoa-go"
                  onClick={() => {
                    const id = versAutre.id;
                    setVersAutre(null);
                    if (id === null) props.choix?.onCreer?.();
                    else props.choix?.onChoisir(id);
                  }}
                >
                  {versAutre.id === null ? "Continuer" : `Changer de ${mot}`}
                </button>
                <button type="button" className="hoa-go2" onClick={() => setVersAutre(null)}>
                  Rester
                </button>
              </div>
            </Feuille>
          ) : null}
          {balise ? <Balise tribe={t} photos={(pid) => photoDe(t.members.find((m) => m.pid === pid))} onFermer={() => setBalise(false)} /> : null}
          {carteOuverte ? (
            <CartePleine
              fix={props.fix}
              cible={t.rendezvous.coords}
              skin={props.skin}
              lieu={`${t.rendezvous.name}${t.rendezvous.landmark ? `, ${t.rendezvous.landmark}` : ""}`}
              onFermer={() => setCarteOuverte(false)}
            />
          ) : null}
          {props.dessus}
        </>
      }
    >
      <Barre>
        <button type="button" className="hoa-rond" aria-label={props.choix?.table ? "Retour aux tables" : "Retour aux tribus"} onClick={props.onRetour}>
          <Retour />
        </button>
        <span className="titre">
          <span className="hoa-titre-tribu">
            <i className="hoa-pastille" style={{ background: t.color }} aria-hidden="true" />
            {t.title} · {membres}
          </span>
        </span>
        <PastillePoints n={props.nPoints} onClick={props.onPoints} />
        <BoutonMoi initiale={props.initiale} ouvert={props.menuOuvert} onClick={props.onMoi} />
      </Barre>
      <div className={arrive && t.phase === "reunie" && rouvert === null ? "hoa-frise hoa-frise-libre" : "hoa-frise"}>
        {etapeTribus}
        {etapeQuestions}
        {etapeChat}
        {etapeRdv}
        {etapeReunis}
      </div>
      {arrive && t.phase === "reunie" && rouvert === null ? (
        <button type="button" className="hoa-go" style={{ marginTop: "auto" }} onClick={props.onTerminer}>
          Terminer
        </button>
      ) : !arrive && t.phase !== "reunie" ? (
        // Toujours à portée de pouce : quitter la tribu (cinq secondes pour se raviser).
        <button type="button" className="hoa-lien hoa-quitter" onClick={props.onQuitter}>
          Quitter la {mot}
        </button>
      ) : null}
    </Ecran>
  );
}

/* ------------------------------------------------------------------ */

/** L'adresse de la photo d'un membre, telle que le serveur la sert. */
function photoDe(m: TribePrivate["members"][number] | undefined): string | null {
  return m?.photo ? `/api/photos/${m.photo}` : null;
}

function Membres(props: { tribe: TribePrivate; arrives?: boolean }): JSX.Element {
  const liste = props.arrives ? props.tribe.members.filter((m) => m.arrive) : props.tribe.members;
  return (
    <div className="hoa-membres" aria-label={props.arrives ? "Arrivés" : "Dans la tribu"}>
      {liste.map((m) => (
        <span key={m.pid} className={m.moi ? "moi" : ""}>
          {m.photo ? <Avatar src={photoDe(m)} nom={m.pseudo} taille={24} /> : null}
          {m.moi ? "Toi" : m.pseudo}
        </span>
      ))}
    </div>
  );
}

function Historique(props: { lignes: LigneChat[]; moi: string | null; tribe: TribePrivate; boite?: boolean }): JSX.Element {
  const membres = new Map(props.tribe.members.map((m) => [m.pid, m]));
  // Le plus récent en premier dans le DOM : `column-reverse` le pose en bas,
  // et la fenêtre reste collée au dernier message sans calcul de défilement.
  const inverse = [...props.lignes].reverse();
  return (
    <div className={props.boite ? "hoa-hist boite" : "hoa-hist"} role="log" aria-live="polite" aria-label="Messages de la tribu" tabIndex={0}>
      {inverse.length === 0 ? <p className="hoa-vide">Personne n'a encore écrit. Lance-toi.</p> : null}
      {inverse.map((l) =>
        l.hoa ? (
          <div key={l.id} className="hoa-msg-hoa">
            {l.text} <span>{heure(l.at)}</span>
          </div>
        ) : (
        <div key={l.id} className={l.pid === props.moi ? "hoa-msg moi" : "hoa-msg"}>
          <b>
            {photoDe(membres.get(l.pid)) ? <Avatar src={photoDe(membres.get(l.pid))} nom={l.pseudo} taille={22} /> : null}
            {l.pid === props.moi ? "Toi" : l.pseudo}
          </b>
          {l.text}
        </div>
        ),
      )}
    </div>
  );
}

/**
 * La saisie du chat : une zone de texte d'une ligne plutôt qu'un champ.
 *
 * Chrome sur Android pose au-dessus du clavier une barre de remplissage
 * automatique (clé, carte bancaire, adresse) sur les champs `<input>`, quel
 * que soit `autocomplete` ; une `<textarea>` ne l'a jamais. Entrée envoie.
 * Tant qu'elle a le focus, l'écran passe en « mode clavier » : seules la
 * conversation et la saisie restent (voir `.hoa-clavier`).
 */
function Saisie(props: { onEnvoyer: (t: string) => void; mot: string }): JSX.Element {
  const [texte, setTexte] = useState("");
  const champ = useRef<HTMLTextAreaElement>(null);
  const envoyer = (): void => {
    const t = texte.trim();
    if (!t) return;
    props.onEnvoyer(t);
    setTexte("");
    champ.current?.focus();
  };
  /* L'écran est retenu au moment du focus : quand la saisie disparaît alors
   * qu'elle a le focus (le chat se replie au passage au rendez-vous), Chrome
   * n'envoie pas de « blur », et au démontage la référence du champ est déjà
   * vide. Sans cela, l'écran restait en mode clavier — toutes les étapes
   * cachées, seul le fond visible (test du 1er octobre, Android). */
  const ecran = useRef<Element | null>(null);
  const clavier = (on: boolean): void => {
    if (on) ecran.current = champ.current?.closest(".hoa-ecran") ?? ecran.current;
    ecran.current?.classList.toggle("hoa-clavier", on);
  };
  useEffect(
    () => () => {
      ecran.current?.classList.remove("hoa-clavier");
    },
    [],
  );
  return (
    // Une <div>, pas un <form> : sur iPhone, un champ dans un formulaire fait
    // apparaître la barre ‹ › ✓ au-dessus du clavier.
    <div className="hoa-saisie" role="group" aria-label={`Écrire à la ${props.mot}`}>
      <textarea
        ref={champ}
        className="hoa-champ hoa-champ-chat"
        aria-label="Message"
        placeholder={`Écrire à la ${props.mot}`}
        maxLength={180}
        rows={1}
        value={texte}
        name="hoa-message"
        autoComplete="off"
        autoCorrect="on"
        autoCapitalize="sentences"
        spellCheck
        enterKeyHint="send"
        onFocus={() => clavier(true)}
        onBlur={() => clavier(false)}
        onChange={(e) => setTexte(e.target.value.replace(/\n/g, " "))}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            envoyer();
          }
        }}
      />
      <button type="button" className="hoa-envoyer" aria-label="Envoyer" onMouseDown={(e) => e.preventDefault()} onClick={envoyer}>
        <Envoyer />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Signaler (H-Signaler)                                               */
/* ------------------------------------------------------------------ */

const MOTIFS: Array<[Motif, string]> = [
  ["propos", "Propos déplacés"],
  ["insistance", "Insistance"],
  ["comportement", "Comportement"],
  ["photo", "Photo de profil"],
  ["autre", "Autre"],
];

function Signaler(props: {
  tribe: TribePrivate;
  chat: LigneChat[];
  onFermer: () => void;
  onEnvoyer: (s: { cible?: string; messageId?: string; motif: Motif; eviter: boolean }) => void;
  onQuitter: () => void;
}): JSX.Element {
  const autres = props.tribe.members.filter((m) => !m.moi);
  const [cible, setCible] = useState<string | null>(null);
  const [motif, setMotif] = useState<Motif | null>(null);
  const [eviter, setEviter] = useState(true);
  return (
    <Feuille label="Signaler" onFermer={props.onFermer}>
      <Parole expression="question">Qu'est-ce qui s'est passé ? Personne ne saura que c'est toi.</Parole>
      <span className="hoa-etiquette">Qui</span>
      <div className="hoa-choix" role="group" aria-label="Qui">
        {autres.map((m) => (
          <button key={m.pid} type="button" aria-pressed={cible === m.pid} onClick={() => setCible(m.pid)}>
            {m.pseudo}
          </button>
        ))}
        <button type="button" aria-pressed={cible === "message"} onClick={() => setCible("message")}>
          Un message
        </button>
      </div>
      <span className="hoa-etiquette">Quoi</span>
      <div className="hoa-choix" role="group" aria-label="Quoi">
        {MOTIFS.map(([id, nom]) => (
          <button key={id} type="button" aria-pressed={motif === id} onClick={() => setMotif(id)}>
            {nom}
          </button>
        ))}
      </div>
      {cible !== "message" ? (
        <label className="hoa-case">
          <input type="checkbox" checked={eviter} onChange={(e) => setEviter(e.target.checked)} />
          Ne plus jamais être dans sa tribu
        </label>
      ) : null}
      <button
        type="button"
        className="hoa-go"
        disabled={!cible || !motif}
        onClick={() =>
          cible &&
          motif &&
          props.onEnvoyer({
            cible: cible === "message" ? undefined : cible,
            messageId: cible === "message" ? props.chat.filter((l) => !l.hoa).at(-1)?.id : undefined,
            motif,
            eviter: cible !== "message" && eviter,
          })
        }
      >
        Envoyer
      </button>
      <button type="button" className="hoa-lien" onClick={props.onQuitter}>
        Quitter la tribu
      </button>
    </Feuille>
  );
}

/* ------------------------------------------------------------------ */
/* Fins de soirée                                                      */
/* ------------------------------------------------------------------ */

export function Annulee(props: {
  skin: SkinId;
  titre: string;
  presents: number;
  minimum: number;
  restantes: number;
  /** « du 2e », « de la régie ». */
  duSecteur: string;
  initiale: string;
  nPoints: number;
  onPoints: () => void;
  menuOuvert: boolean;
  dessus?: ReactNode;
  onMoi: () => void;
  onTribus: () => void;
}): JSX.Element {
  return (
    <Ecran skin={props.skin} label="Tribu annulée" dessus={props.dessus}>
      <Barre>
        <button type="button" className="hoa-rond" aria-label="Retour aux tribus" onClick={props.onTribus}>
          <Retour />
        </button>
        <span className="titre">{props.titre} · annulée</span>
        <PastillePoints n={props.nPoints} onClick={props.onPoints} />
        <BoutonMoi initiale={props.initiale} ouvert={props.menuOuvert} onClick={props.onMoi} />
      </Barre>
      <div className="hoa-frise">
        <Etape etat="courante" expression="question">
          <Bulle>
            « {props.titre} » ne part pas : vous n'étiez que {props.presents}, il en fallait {props.minimum}. Personne ne
            t'attend.
            {props.restantes > 0
              ? ` ${props.restantes === 1 ? "Une tribu part" : `${props.restantes} tribus partent`} encore ce soir.`
              : ""}
          </Bulle>
          <button type="button" className="hoa-go" style={{ marginTop: 12 }} onClick={props.onTribus}>
            Voir les tribus {props.duSecteur}
          </button>
        </Etape>
        <Etape etat="barree">
          <Libelle barre>Chat de la tribu</Libelle>
        </Etape>
        <Etape etat="barree">
          <Libelle barre>Rendez-vous</Libelle>
        </Etape>
        <Etape etat="barree" dernier>
          <Libelle barre>Réunis</Libelle>
        </Etape>
      </div>
    </Ecran>
  );
}


export { Moai };
